/**
 * OAuth redirect seam (architecture §5). The server's `beginLink` returns a provider `authUrl` +
 * `state`; something must open it, let the user consent, and capture the `?code=&state=` the
 * provider sends to the registered redirect URI. That capture differs per shell — a browser popup
 * on the web, a deep link in Tauri — so it lives behind this port. Today only the web impl is wired
 * (decision: "web now, desktop behind a seam"); `createTauriDeepLinkRedirect` marks where the
 * desktop variant slots in.
 */
export interface OAuthCallback {
  code: string;
  state: string;
}

export interface RedirectHandler {
  /** Open `authUrl`, resolve with the callback once the provider returns matching `state`. */
  authorize(authUrl: string, state: string): Promise<OAuthCallback>;
}

/** The postMessage envelope the callback page sends back to the opener. */
export interface CallbackMessage {
  type: 'lapo-oauth-callback';
  code?: string;
  state?: string;
  error?: string;
}

export const CALLBACK_MESSAGE_TYPE = 'lapo-oauth-callback';
/** Path the provider redirect URI must point at (registered in the Google/Apple console). */
export const CALLBACK_PATH = '/oauth/callback';

/** Parse a callback query string (`?code=…&state=…` or `?error=…`) into a structured result. */
export function parseCallbackParams(search: string): CallbackMessage {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const state = p.get('state') ?? undefined;
  const error = p.get('error');
  // Keep `state` on error responses too (OAuth returns it): lets the handler correlate the failure
  // to the request that's waiting, instead of hanging until timeout.
  if (error) return { type: CALLBACK_MESSAGE_TYPE, error, state };
  return {
    type: CALLBACK_MESSAGE_TYPE,
    code: p.get('code') ?? undefined,
    state,
  };
}

/** Does a received message complete the flow for `state`? Used by the popup handler (and tests). */
export function matchesCallback(data: unknown, state: string): data is CallbackMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as CallbackMessage).type === CALLBACK_MESSAGE_TYPE &&
    (data as CallbackMessage).state === state
  );
}

/**
 * Run on the `/oauth/callback` page: forward the query result to the opener and close. No app UI is
 * mounted there. Returns true if it posted to an opener (so the caller knows not to mount the app).
 */
export function deliverCallbackToOpener(win: Window = window): boolean {
  const opener = win.opener as Window | null;
  if (!opener) return false;
  const msg = parseCallbackParams(win.location.search);
  opener.postMessage(msg, win.location.origin);
  win.close();
  return true;
}

/** Custom scheme the native shells register (matches `plugins.deep-link.schemes` + the Android
 *  intent filter). Also the Android application id / package. */
export const DESKTOP_CALLBACK_SCHEME = 'app.lapo.client';
/** Custom scheme the desktop shell registers (matches `plugins.deep-link.desktop.schemes`). */
export const DESKTOP_CALLBACK_URL = `${DESKTOP_CALLBACK_SCHEME}://oauth/callback`;

/**
 * Build the URL that hands the OAuth callback back to the installed native app.
 *
 * macOS Safari (the desktop system browser) honors a plain `app.lapo.client://…` navigation and
 * launches Lapo. Android's Chrome does NOT: it silently drops a JS-initiated `location.href` to an
 * unknown scheme (no user gesture → `ERR_UNKNOWN_URL_SCHEME`/no-op), which is exactly why the
 * Android connect flow reached "you can close this window" but never returned to the app. Android's
 * supported mechanism is an `intent://…#Intent;scheme=…;package=…;end` URL, so we emit that form
 * there. The scheme-only intent filter ignores the host/path — the app reads only the query string —
 * so any `host/path` works as the intent data.
 */
export function buildNativeCallbackUrl(search: string, ua = navigatorUserAgent()): string {
  const query = search || '';
  if (/Android/i.test(ua)) {
    return `intent://oauth/callback${query}#Intent;scheme=${DESKTOP_CALLBACK_SCHEME};package=${DESKTOP_CALLBACK_SCHEME};end`;
  }
  return `${DESKTOP_CALLBACK_URL}${query}`;
}

function navigatorUserAgent(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
}

/**
 * Relay an OAuth callback into the native app. The mobile/desktop flow opens the provider consent in
 * the SYSTEM browser, so the provider redirects to the same HTTPS `/oauth/callback` page — but that
 * tab has no `window.opener` (unlike the web popup). When {@link deliverCallbackToOpener} finds no
 * opener, this hands the untouched `?code=&state=` query to the running app via its custom scheme
 * (macOS) or an Android intent URL, where the deep-link handler resolves the pending link. One
 * deployed callback page serves every shell with NO new provider redirect URI. Harmless no-op in a
 * plain browser where the app isn't installed.
 */
export function relayCallbackToDesktopScheme(win: Window = window): void {
  win.location.href = buildNativeCallbackUrl(win.location.search, win.navigator?.userAgent);
}

/** Prefix the server's link-broker session ids (and thus the OAuth `state`) carry, so the callback
 *  page can distinguish a mobile server-brokered link from a desktop deep-link flow. MUST match the
 *  `lb_` prefix minted by the server's LinkBrokerService. */
export const BROKER_STATE_PREFIX = 'lb_';

/** True when the `/oauth/callback` query belongs to a server-brokered mobile link (deposit, not relay). */
export function isBrokeredCallback(search: string): boolean {
  return (parseCallbackParams(search).state ?? '').startsWith(BROKER_STATE_PREFIX);
}

/**
 * Run on the `/oauth/callback` page for a mobile server-brokered link: POST the `{state, code}` to the
 * server broker, which does the exchange and links the account (the app polls for completion). This
 * replaces the unreliable Android deep-link handoff — the code goes app→server via the browser, never
 * back through a custom scheme. Reuses the same callback page + redirect URI as every other flow.
 */
export async function depositBrokeredCallback(apiUrl: string, search: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const { code, state, error } = parseCallbackParams(search);
  if (error) throw new Error(error);
  if (!state || !code) throw new Error('missing code/state');
  const base = apiUrl.replace(/\/$/, '');
  const res = await fetchImpl(`${base}/accounts/link/broker/deposit`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ state, code }),
  });
  if (!res.ok) throw new Error(`Could not finish connecting (${res.status})`);
}

export interface WebPopupOptions {
  win?: Window;
  /** Popup features; small centered window by default. */
  features?: string;
}

/** Web RedirectHandler: opens a popup and waits for the callback page's postMessage. */
export function createWebPopupRedirect(opts: WebPopupOptions = {}): RedirectHandler {
  const win = opts.win ?? window;
  const features = opts.features ?? 'width=520,height=660,menubar=no,toolbar=no';
  return {
    authorize(authUrl, state) {
      return new Promise<OAuthCallback>((resolve, reject) => {
        const popup = win.open(authUrl, 'lapo-oauth', features);
        if (!popup) {
          reject(new Error('Could not open sign-in window (popup blocked?).'));
          return;
        }
        const cleanup = () => {
          win.removeEventListener('message', onMessage);
          clearInterval(poll);
        };
        const onMessage = (e: MessageEvent) => {
          if (e.origin !== win.location.origin) return;
          if (!matchesCallback(e.data, state)) return;
          cleanup();
          popup.close();
          const { code, error } = e.data;
          if (error) reject(new Error(`OAuth failed: ${error}`));
          else if (code) resolve({ code, state });
          else reject(new Error('OAuth callback missing code'));
        };
        win.addEventListener('message', onMessage);
        // Detect a user closing the popup without finishing.
        const poll = win.setInterval(() => {
          if (popup.closed) {
            cleanup();
            reject(new Error('Sign-in window closed before completing.'));
          }
        }, 500);
      });
    },
  };
}

/**
 * Desktop (Tauri) redirect handler for integration linking. Opens the provider consent in the SYSTEM
 * browser and resolves from the registered `app.lapo.client://oauth/callback` deep link
 * (tauri-plugin-deep-link). The server's `/oauth/callback` page detects a desktop-originated flow and
 * 302-redirects into that scheme, so no new provider redirect URI is required — the provider keeps
 * redirecting to the existing HTTPS callback.
 *
 * Tauri plugin modules are imported dynamically so the web bundle never eagerly loads native-only
 * code; this function is only ever selected when the shell is Tauri.
 */
export function createTauriDeepLinkRedirect(): RedirectHandler {
  return {
    async authorize(authUrl, state) {
      const [{ onOpenUrl }, { openUrl }] = await Promise.all([
        import('@tauri-apps/plugin-deep-link'),
        import('@tauri-apps/plugin-opener'),
      ]);
      return new Promise<OAuthCallback>((resolve, reject) => {
        let unlisten: (() => void) | undefined;
        let settled = false;
        const finish = (fn: () => void) => {
          if (settled) return;
          settled = true;
          unlisten?.();
          fn();
        };
        // Register the deep-link listener BEFORE opening the browser so a fast redirect isn't missed.
        onOpenUrl((urls) => {
          for (const raw of urls) {
            let search = '';
            try {
              search = new URL(raw).search;
            } catch {
              continue; // not a URL we can parse — ignore
            }
            const msg = parseCallbackParams(search);
            if (!matchesCallback(msg, state)) continue; // different flow / stale link
            if (msg.error) finish(() => reject(new Error(`OAuth failed: ${msg.error}`)));
            else if (msg.code) finish(() => resolve({ code: msg.code as string, state }));
            else finish(() => reject(new Error('OAuth callback missing code')));
            return;
          }
        }).then(
          (u) => {
            unlisten = u;
            if (settled) u(); // flow already completed before the listener resolved
          },
          (e) => finish(() => reject(new Error(`Could not listen for sign-in redirect: ${e}`))),
        );
        openUrl(authUrl).catch((e) =>
          finish(() => reject(new Error(`Could not open the sign-in browser: ${e}`))),
        );
      });
    },
  };
}

/**
 * One-shot link orchestration shared by the Settings "Connect" buttons: begin on the server, run
 * the shell's redirect, then complete the link. Provider-agnostic and shell-agnostic.
 */
export async function runLink(
  api: { beginLink(provider: 'google' | 'apple', scopes?: string[]): Promise<{ authUrl: string; state: string }>; completeLink(state: string, code: string): Promise<unknown> },
  redirect: RedirectHandler,
  provider: 'google' | 'apple',
  scopes: string[] = [],
  onBegin?: (state: string) => void,
): Promise<unknown> {
  const { authUrl, state } = await api.beginLink(provider, scopes);
  onBegin?.(state); // persist a cold-start-safe record before the browser round-trip (native)
  const cb = await redirect.authorize(authUrl, state);
  return api.completeLink(state, cb.code);
}

/**
 * Gmail Cleanup delete-permission grant — the same begin → redirect → complete shape as {@link runLink},
 * but it begins on `/accounts/gmail-modify` (which requests the base scopes + gmail.modify server-side)
 * and completes via the shared Google `completeLink`. Re-consent reuses the existing account by
 * (provider, externalId), so its id/refresh token are preserved — only the scopes grow.
 */
export async function runGmailModifyGrant(
  api: { beginGmailModify(): Promise<{ authUrl: string; state: string }>; completeLink(state: string, code: string): Promise<unknown> },
  redirect: RedirectHandler,
  onBegin?: (state: string) => void,
): Promise<unknown> {
  const { authUrl, state } = await api.beginGmailModify();
  onBegin?.(state);
  const cb = await redirect.authorize(authUrl, state);
  return api.completeLink(state, cb.code);
}

/**
 * Todoist link orchestration — the same begin → redirect → complete shape as {@link runLink}, but on
 * the separate Todoist OAuth endpoints. Reuses the very same RedirectHandler and `/oauth/callback`
 * page (the flow correlates by `state`, so the callback page is provider-agnostic).
 */
export async function runTodoistLink(
  api: { beginTodoistLink(): Promise<{ authUrl: string; state: string }>; completeTodoistLink(state: string, code: string): Promise<unknown> },
  redirect: RedirectHandler,
  onBegin?: (state: string) => void,
): Promise<unknown> {
  const { authUrl, state } = await api.beginTodoistLink();
  onBegin?.(state);
  const cb = await redirect.authorize(authUrl, state);
  return api.completeTodoistLink(state, cb.code);
}

/**
 * Tesseta (Fitness) link orchestration — same begin → redirect → complete shape as {@link runTodoistLink}.
 * The PKCE `code_verifier` is held server-side (keyed by `state`), so the browser only carries `state`;
 * this reuses the very same RedirectHandler and `/oauth/callback` page.
 */
export async function runTessetaLink(
  api: { beginTessetaLink(): Promise<{ authUrl: string; state: string }>; completeTessetaLink(state: string, code: string): Promise<unknown> },
  redirect: RedirectHandler,
  onBegin?: (state: string) => void,
): Promise<unknown> {
  const { authUrl, state } = await api.beginTessetaLink();
  onBegin?.(state);
  const cb = await redirect.authorize(authUrl, state);
  return api.completeTessetaLink(state, cb.code);
}

/** Thread the Slack user-token OAuth link: begin → popup → complete (mirrors {@link runLink}). */
export async function runSlackLink(
  api: {
    startSlackLink(): Promise<{ authUrl: string; state: string }>;
    completeSlackLink(input: { state: string; code: string }): Promise<unknown>;
  },
  redirect: RedirectHandler,
  onBegin?: (state: string) => void,
): Promise<unknown> {
  const { authUrl, state } = await api.startSlackLink();
  onBegin?.(state);
  const cb = await redirect.authorize(authUrl, state);
  return api.completeSlackLink({ state, code: cb.code });
}
